/**
 * retry.ts
 *
 * Retries transient request failures with exponential backoff.
 */
import type { Interceptor } from "@connectrpc/connect";
import { getRetryDelay, isRetryableError } from "@/services/retryPolicy";
import { logger } from "@/utils/logger";

/** Real wall-clock sleep, used when no override is supplied. */
const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Dependencies of the retry interceptor. */
export interface RetryInterceptorDeps {
  /** Retries after the initial attempt. Defaults to 2, i.e. 3 attempts total. */
  maxRetries?: number;
  /** Delay function. Inject a fake in tests to avoid real backoff waits. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Builds the retry interceptor.
 *
 * Retries only failures classified as transient by
 * {@link isRetryableError}, waiting {@link getRetryDelay} between attempts.
 * Terminal failures are rethrown immediately without sleeping.
 *
 * @param deps - Optional max-retry count and sleep override.
 * @returns A Connect interceptor.
 */
export function createRetryInterceptor(
  deps: RetryInterceptorDeps = {},
): Interceptor {
  const { maxRetries = 2, sleep = defaultSleep } = deps;

  return (next) => async (req) => {
    let lastError: unknown;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await next(req);
      } catch (error) {
        lastError = error;

        // Check if error is retryable (network/temporary)
        const shouldRetry = isRetryableError(error) && attempt < maxRetries;

        if (shouldRetry) {
          const delay = getRetryDelay(attempt);
          logger.debug(
            `Request failed (attempt ${attempt + 1}/${
              maxRetries + 1
            }), retrying in ${delay}ms...`,
            {
              method: req.url.split("/").pop(),
              error: error instanceof Error ? error.message : "Unknown error",
            },
          );

          await sleep(delay);
          continue; // Retry the request
        }

        // Not retryable or max retries reached
        throw error;
      }
    }

    // Should never reach here, but TypeScript needs it
    throw lastError;
  };
}
